import React from "react";
import { ref, remove, onValue } from "firebase/database";
import { db } from "./firebase";
import { generateStudentReport } from "./StudentReport";
import { FaWhatsapp } from "react-icons/fa";
import { formatCourseId } from "./utils/formatCourse";

const StudentsTable = ({ students, activeCourse, searchQuery, onDelete }) => {
  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchQuery ||
      ['name', 'email', 'phone', 'parentPhone', 'studentId'].some(field =>
        String(student[field] || '').toLowerCase().includes(searchQuery.toLowerCase())
      );

    return matchesSearch;
  });

  const handleDelete = async (studentId) => {
    if (!window.confirm(`Are you sure you want to delete student ${studentId}?`)) {
      return;
    }

    try {
      await remove(ref(db, `courses/${activeCourse}/students/${studentId}`));
      alert(`Student ${studentId} deleted successfully`);
      onDelete(); // Notify parent if needed
    } catch (error) {
      alert("Error deleting student: " + error.message);
    }
  };

  const formatPhoneNumber = (phone) => {
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Add the country code if not present (assuming Egypt +20)
    return cleaned.startsWith('20') ? cleaned : `20${cleaned}`;
  };

  const getWhatsAppLink = (phone) => {
    if (!phone) return null;
    const formattedNumber = formatPhoneNumber(phone);
    return `https://wa.me/${formattedNumber}`;
  };

  const sendReportViaWhatsApp = async (student, phone) => {
    try {
      // Fetch student data for the report
      const reportData = await getStudentReportData(student);

      // Create WhatsApp link with the report as text
      const formattedNumber = formatPhoneNumber(phone);
      const message = reportData;
      const encodedMessage = encodeURIComponent(message);

      const whatsappLink = `https://wa.me/${formattedNumber}?text=${encodedMessage}`;
      window.open(whatsappLink, '_blank');

    } catch (error) {
      alert("Error generating report: " + error.message);
    }
  };

  const getStudentReportData = async (student) => {
    // Helper to fetch data from a ref and return a promise
    const fetchData = (refPath) => {
      return new Promise((resolve) => {
        const r = ref(db, refPath);
        onValue(r, (snapshot) => {
          resolve(snapshot.exists() ? snapshot.val() : {});
        }, { onlyOnce: true });
      });
    };

    // Fetch homeworks and exams for the student's course
    const [homeworks, exams] = await Promise.all([
      fetchData(`courses/${student.course}/homeworks`),
      fetchData(`courses/${student.course}/exams`)
    ]);

    // Build the report as text
    let report = `STUDENT REPORT\n\n`;
    report += `Student Information\n`;
    report += `Name: ${student.name}\n`;
    report += `ID: ${student.studentId}\n`;
    report += `Email: ${student.email || "N/A"}\n`;
    report += `Course: ${formatCourseId(student.course || student.enrolledCourse || "N/A")}\n`;
    report += `Date: ${new Date().toLocaleDateString()}\n\n`;

    // Exam Grades
    const examEntries = exams && Object.keys(exams).length > 0 ? Object.entries(exams) : [];
    if (examEntries.length > 0) {
      report += `EXAM GRADES\n`;
      examEntries.forEach(([examId, exam]) => {
        const studentExam = student.examGrades && student.examGrades[examId] ? student.examGrades[examId] : {};
        report += `\n${exam.name || examId}\n`;
        report += `Date: ${exam.date || "N/A"}\n`;
        report += `Grade: ${studentExam.grade || "N/A"}\n`;
        report += `Mark: ${studentExam.mark || "N/A"}/${exam.totalMark || "N/A"}\n`;
        if (studentExam.comment && studentExam.comment !== "N/A") {
          report += `Comment: ${studentExam.comment}\n`;
        }
      });
    } else {
      report += `EXAM GRADES\nNo exam grades available\n`;
    }

    // Homework Status
    const currentDate = new Date();
    const homeworkEntries = homeworks && Object.keys(homeworks).length > 0 ? Object.entries(homeworks) : [];
    if (homeworkEntries.length > 0) {
      report += `\n-----------------------------------\n`;
      report += `\nHOMEWORK STATUS\n`;
      homeworkEntries.forEach(([hwId, hw]) => {
        const status = student.homeworkStatus && student.homeworkStatus[hwId] ? student.homeworkStatus[hwId] : "missing";
        report += `\n${hw.title || hwId}\n`;
        report += `Due: ${hw.dueDate ? formatDate(hw.dueDate) : "No due date"}\n`;
        report += `Status: ${formatStatus(status)}\n`;
      });
    } else {
      report += `\n-----------------------------------\n`;
      report += `\nHOMEWORK STATUS\nNo homework assignments available\n`;
    }

    return report;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? dateString
      : `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  // Helper function to format status
  const formatStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'done':
        return 'Done';
      case 'incomplete':
        return 'Incomplete';
      case 'missing':
        return 'Missing';
      default:
        return status;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Student ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Phone
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Parent Phone
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredStudents.map((student) => (
            <tr key={student.studentId}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {student.studentId}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {student.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {student.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {student.phone}
                {student.phone && (
                  <button
                    onClick={() => sendReportViaWhatsApp(student, student.phone)}
                    className="ml-2 text-green-600 hover:text-green-800"
                    title="Send report via WhatsApp"
                  >
                    <FaWhatsapp />
                  </button>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {student.parentPhone}
                {student.parentPhone && (
                  <button
                    onClick={() => sendReportViaWhatsApp(student, student.parentPhone)}
                    className="ml-2 text-green-600 hover:text-green-800"
                    title="Send report via WhatsApp"
                  >
                    <FaWhatsapp />
                  </button>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex space-x-2">
                  <button
                    onClick={() => generateStudentReport(student)}
                    className="text-teal-600 hover:text-teal-800"
                  >
                    Report
                  </button>
                  <button
                    onClick={() => handleDelete(student.studentId)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentsTable;